// Projektets samlade kemikalieförteckning med alla inlämnade säkerhetsdatablad.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlaskConical, FileDown, RefreshCw, Eye, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DocumentHeader from "@/components/BasProcess/DocumentHeader";
import { generateSafetyDataSheetListWord } from "@/lib/documentGenerator/granskningWordGenerator";

interface Props {
  projectId: string;
  projectName?: string;
  canExport?: boolean;
}

interface Row {
  id: string;
  product_name: string;
  supplier: string | null;
  usage_area: string | null;
  quantity: string | null;
  hazard_codes: string | null;
  protective_measures: string | null;
  contractor: string | null;
  file_name: string | null;
  file_path: string | null;
  created_at: string;
  work_preparation_id: string | null;
}

const SafetyDataSheetRegister: React.FC<Props> = ({ projectId, projectName, canExport = true }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [preps, setPreps] = useState<Record<string, string>>({});
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [sds, wp] = await Promise.all([
      supabase
        .from("safety_data_sheets")
        .select(
          "id, product_name, supplier, usage_area, quantity, hazard_codes, protective_measures, contractor, file_name, file_path, created_at, work_preparation_id"
        )
        .eq("project_id", projectId)
        .order("product_name", { ascending: true }),
      supabase.from("work_preparations").select("id, name").eq("project_id", projectId),
    ]);
    setRows((sds.data ?? []) as Row[]);
    setPreps(Object.fromEntries((wp.data ?? []).map((w: any) => [w.id, w.name])));
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.product_name} ${r.supplier ?? ""} ${r.contractor ?? ""} ${r.hazard_codes ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const missing = rows.filter((r) => !r.file_path).length;

  const openFile = async (r: Row) => {
    if (!r.file_path) return;
    const { data } = await supabase.storage.from("sakerhetsdatablad").createSignedUrl(r.file_path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  };

  const exportWord = async () => {
    try {
      await generateSafetyDataSheetListWord({
        rows: filtered.map((r) => ({
          productName: r.product_name,
          supplier: r.supplier ?? "",
          usageArea: r.usage_area ?? "",
          quantity: r.quantity ?? "",
          hazardCodes: r.hazard_codes ?? "",
          protectiveMeasures: r.protective_measures ?? "",
          contractor: r.contractor ?? "",
          workPreparation: r.work_preparation_id ? preps[r.work_preparation_id] ?? "" : "",
          fileName: r.file_name ?? "",
          createdAt: r.created_at,
          changedBy: r.contractor ?? "",
        })),
        projectName,
      });
      toast.success("Kemikalieförteckningen exporterad");
    } catch {
      toast.error("Kunde inte exportera kemikalieförteckningen");
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="26"
        exportHistory={{ documentType: "safety_data_sheets" }}
        title="Kemikalieförteckning och säkerhetsdatablad"
        description="Alla kemiska produkter som entreprenörerna lämnat in via sina arbetsberedningar, samlade på ett ställe."
        legalRef="AFS 2023:10"
        icon={FlaskConical}
        projectId={projectId}
        meta={
          <div className="flex items-center gap-2">
            <Badge variant="outline">{rows.length} produkter</Badge>
            {missing > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> {missing} saknar datablad
              </Badge>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Uppdatera
            </Button>
            {canExport && (
              <Button size="sm" onClick={exportWord}>
                <FileDown className="mr-1 h-4 w-4" /> Exportera till Word
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Produkter</CardTitle>
          <Input
            className="max-w-xs"
            placeholder="Sök produkt, leverantör eller entreprenör"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </CardHeader>
        <CardContent className="space-y-2">
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga kemiska produkter är inlämnade ännu. De registreras i arbetsberedningarna och hamnar automatiskt
              här.
            </p>
          )}
          {filtered.map((r) => (
            <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 rounded-md border p-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium">{r.product_name}</div>
                <div className="text-xs text-muted-foreground">
                  {[r.supplier, r.usage_area, r.quantity, r.contractor].filter(Boolean).join(" · ") || "–"}
                </div>
                {r.hazard_codes && <div className="mt-1 text-xs">Faromärkning: {r.hazard_codes}</div>}
                {r.protective_measures && (
                  <div className="text-xs text-muted-foreground">Skydd: {r.protective_measures}</div>
                )}
                {r.work_preparation_id && preps[r.work_preparation_id] && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Från arbetsberedning: {preps[r.work_preparation_id]}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {r.file_path ? (
                  <>
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Datablad</Badge>
                    <Button size="sm" variant="ghost" onClick={() => openFile(r)}>
                      <Eye className="mr-1 h-4 w-4" /> Öppna
                    </Button>
                  </>
                ) : (
                  <Badge variant="destructive">Datablad saknas</Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default SafetyDataSheetRegister;
